import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties, ReactNode } from 'react';

export function SortableList({
  ids,
  onReorder,
  children,
  ariaLabel,
  orientation = 'vertical',
}: {
  ids: readonly string[];
  onReorder: (activeId: string, overId: string) => void;
  children: ReactNode;
  ariaLabel: string;
  orientation?: 'vertical' | 'horizontal';
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : undefined;
    if (!overId || activeId === overId) return;
    onReorder(activeId, overId);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={[...ids]}
        strategy={
          orientation === 'horizontal' ? horizontalListSortingStrategy : verticalListSortingStrategy
        }
      >
        <div aria-label={ariaLabel}>{children}</div>
      </SortableContext>
    </DndContext>
  );
}

export function SortableItem({
  id,
  className,
  children,
  disabled = false,
}: {
  id: string;
  className?: string;
  children: (dragHandle: ReactNode) => ReactNode;
  disabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.55 } : {}),
  };

  const dragHandle = (
    <button
      type="button"
      className="builder-drag-handle"
      aria-label="Drag to reorder"
      title="Drag to reorder"
      {...attributes}
      {...listeners}
    >
      <span aria-hidden="true">⋮⋮</span>
    </button>
  );

  return (
    <div ref={setNodeRef} style={style} className={className}>
      {children(dragHandle)}
    </div>
  );
}
